export const LAST_AUTH_STORAGE_KEY = 'twine_last_auth';

export const LAST_AUTH_METHODS = {
  GOOGLE: 'google',
  PASSWORD: 'password',
  CODE: 'code',
};

const METHODS_WITH_IDENTIFIER = new Set([
  LAST_AUTH_METHODS.PASSWORD,
  LAST_AUTH_METHODS.CODE,
]);

function normalizeIdentifier(value) {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
}

/**
 * Read the last-used auth method hint from localStorage.
 * Email/username is persisted only for password and code methods.
 * @returns {{ method?: string, email?: string, at?: number } | null}
 */
export function getLastAuth() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const raw = window.localStorage.getItem(LAST_AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const method =
      typeof parsed.method === 'string' ? parsed.method : undefined;
    const at = typeof parsed.at === 'number' ? parsed.at : undefined;
    const email = METHODS_WITH_IDENTIFIER.has(method)
      ? normalizeIdentifier(parsed.email)
      : undefined;

    if (!method) {
      // Drop identifier-only legacy rows that cannot show a last-used method.
      if (Object.prototype.hasOwnProperty.call(parsed, 'email')) {
        window.localStorage.removeItem(LAST_AUTH_STORAGE_KEY);
      }
      return null;
    }

    // Rewrite Google (or other) rows that still carry a leftover email.
    if (
      !METHODS_WITH_IDENTIFIER.has(method) &&
      Object.prototype.hasOwnProperty.call(parsed, 'email')
    ) {
      window.localStorage.setItem(
        LAST_AUTH_STORAGE_KEY,
        JSON.stringify({ method, at: at || Date.now() })
      );
    }

    return email ? { method, email, at } : { method, at };
  } catch (e) {
    return null;
  }
}

/**
 * Persist last-used sign-in method.
 * Email/username is stored only for password and code methods.
 * @param {{ method?: string, email?: string }} next
 */
export function setLastAuth(next = {}) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const current = getLastAuth() || {};
    const method = next.method || current.method;

    if (!method) {
      return;
    }

    const payload = {
      method,
      at: Date.now(),
    };

    if (METHODS_WITH_IDENTIFIER.has(method)) {
      const email =
        normalizeIdentifier(next.email) ||
        (METHODS_WITH_IDENTIFIER.has(current.method)
          ? current.email
          : undefined);
      if (email) {
        payload.email = email;
      }
    }

    window.localStorage.setItem(LAST_AUTH_STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // Ignore quota / private mode failures
  }
}
